import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  CircularProgress,
  Alert,
  Box,
} from '@mui/material';
import { coursesAPI } from '../services/api';
import { toast } from 'react-toastify';
import Button from '../components/UIKit/Button';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import TestDetail from '../components/TestDetail';

const TestPreviewPage = () => {
  const { t } = useTranslation(['tests', 'common']);
  const { courseSlug, moduleSlug, testId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth(); // Получаем данные пользователя из контекста
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [courseData, setCourseData] = useState(null);
  const [currentModule, setCurrentModule] = useState(null);
  const [currentTest, setCurrentTest] = useState(null);
  const [moduleIndex, setModuleIndex] = useState(null);
  // --- Реф для программного управления TestDetail (онбординг) ---
  const testDetailRef = useRef(null);

  const isAdmin = user?.userMeta?.roles?.includes('admin') || user?.roles?.includes('admin');
  const isTeacher = user?.userMeta?.roles?.includes('teacher') || user?.roles?.includes('teacher');
  const hasAccess = isAdmin || isTeacher;

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    if (!hasAccess) {
      setError(t('common:errors.accessDenied'));
      setLoading(false);
      return;
    }
  }, [user, hasAccess, t]);

  // Загрузка данных о курсе для навигации между тестами (только один раз при монтировании)
  useEffect(() => {
    const fetchCourseData = async () => {
      if (!hasAccess) {
        return;
      }

      // Admin mode: no course context needed
      if (!courseSlug) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const courseResponse = await coursesAPI.getBySlugWithProfessions(courseSlug);
        setCourseData(courseResponse.data);

        // Находим текущий модуль и тест
        if (courseResponse.data && courseResponse.data.modules) {
          const modules = courseResponse.data.modules;

          // Находим индекс текущего модуля
          const modIndex = modules.findIndex(
            (module) => (module.module ? module.module.slug : module.slug) === moduleSlug
          );
          if (modIndex !== -1) {
            setCurrentModule(modules[modIndex]);
            setModuleIndex(modIndex);

            // Находим тест в уроках модуля (новая логика: тесты теперь внутри уроков)
            const moduleData = modules[modIndex].module || modules[modIndex];
            const lessons = moduleData.lessons || [];
            let foundTest = null;

            // Ищем тест во всех уроках модуля
            for (const lesson of lessons) {
              if (lesson.tests && Array.isArray(lesson.tests)) {
                const testIdx = lesson.tests.findIndex((test) => test._id === testId);
                if (testIdx !== -1) {
                  foundTest = lesson.tests[testIdx];
                  break;
                }
              }
            }

            if (foundTest) {
              setCurrentTest(foundTest);
            } else {
              setError(t('tests:testDetail.testPreviewPage.errors.testNotFound'));
            }
          } else {
            setError(t('tests:testDetail.testPreviewPage.errors.moduleNotFound'));
          }
        } else {
          setError(t('tests:testDetail.testPreviewPage.errors.invalidCourseData'));
        }
      } catch (err) {
        console.error('Ошибка при загрузке данных курса:', err);
        setError(
          err.response?.data?.message || t('common:errors.loadError')
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCourseData();
  }, [courseSlug, testId, moduleSlug, t, user, hasAccess]);


  // При изменении moduleSlug или testId обновляем текущие индексы и данные текущего модуля/теста
  useEffect(() => {
    if (courseData && courseData.modules) {
      // Обновляем индекс модуля и сам модуль
      const modIndex = courseData.modules.findIndex((m) => (m.module ? m.module.slug : m.slug) === moduleSlug);
      if (modIndex !== -1) {
        setModuleIndex(modIndex);
        setCurrentModule(courseData.modules[modIndex]);

        // Обновляем тест
        // В новой структуре тесты находятся внутри уроков
        const moduleData = courseData.modules[modIndex].module || courseData.modules[modIndex];
        const lessons = moduleData.lessons || [];
        let foundTest = null;

        // Ищем тест во всех уроках модуля
        for (const lesson of lessons) {
          if (lesson.tests && Array.isArray(lesson.tests)) {
            const testIdx = lesson.tests.findIndex((t) => t._id === testId);
            if (testIdx !== -1) {
              foundTest = lesson.tests[testIdx];
              break;
            }
          }
        }

        if (foundTest) {
          setCurrentTest(foundTest);
        } else {
          setCurrentTest(null);
        }
      } else {
        setModuleIndex(null);
        setCurrentModule(null);
        setCurrentTest(null);
      }
    }
  }, [courseData, moduleSlug, testId]);

  // Обработчик для возврата к странице курса
  const handleBackToCourse = () => {
    if (courseSlug) {
      navigate(`/courses/${courseSlug}`);
    } else {
      navigate(-1);
    }
  };

  // Helper function to collect all tests from lessons in a module
  const getAllTestsFromModule = (moduleData) => {
    const lessons = moduleData.lessons || [];
    const allTests = [];

    lessons.forEach((lesson) => {
      if (lesson.tests && Array.isArray(lesson.tests)) {
        lesson.tests.forEach((test) => {
          allTests.push(test);
        });
      }
    });

    return allTests;
  };

  // Переход к следующему тесту или уроку
  const handleNextTest = async () => {
    if (
      !courseData ||
      !courseData.modules ||
      moduleIndex === null ||
      !currentTest
    )
      return;

    const currentModule = courseData.modules[moduleIndex].module || courseData.modules[moduleIndex];
    const currentModuleTests = getAllTestsFromModule(currentModule);

    // Находим текущий тест в списке всех тестов модуля
    const currentTestIdxInModule = currentModuleTests.findIndex(t => t._id === currentTest._id);

    // Проверяем, есть ли следующий тест в текущем модуле
    if (currentTestIdxInModule < currentModuleTests.length - 1) {
      const nextTestId = currentModuleTests[currentTestIdxInModule + 1]._id;
      navigate(
        `/courses/${courseData.slug}/modules/${moduleSlug}/tests/${nextTestId}/preview`
      );
      return;
    }

    // Если это последний тест в модуле, ищем следующий урок или тест в следующих модулях
    proceedToNextModuleOrLesson(courseData);
  };

  // Переход к предыдущему тесту или уроку
  const handlePrevTest = () => {
    if (
      !courseData ||
      !courseData.modules ||
      moduleIndex === null ||
      !currentTest
    )
      return;

    const currentModule = courseData.modules[moduleIndex].module || courseData.modules[moduleIndex];
    const currentModuleTests = getAllTestsFromModule(currentModule);

    // Находим текущий тест в списке всех тестов модуля
    const currentTestIdxInModule = currentModuleTests.findIndex(t => t._id === currentTest._id);

    // Проверяем, есть ли предыдущий тест в текущем модуле
    if (currentTestIdxInModule > 0) {
      const prevTestId = currentModuleTests[currentTestIdxInModule - 1]._id;
      navigate(
        `/courses/${courseData.slug}/modules/${moduleSlug}/tests/${prevTestId}/preview`
      );
      return;
    }

    // Если это первый тест в модуле, ищем последний урок в текущем модуле
    if (currentModule?.lessons && currentModule.lessons.length > 0) {
      const lastLessonIndex = currentModule.lessons.length - 1;
      const prevLesson = currentModule.lessons[lastLessonIndex];
      const prevLessonSlug = prevLesson.slug || prevLesson._id;
      navigate(
        `/courses/${courseData.slug}/modules/${moduleSlug}/lessons/${prevLessonSlug}`
      );
      return;
    }

    // Ищем предыдущий элемент в предыдущих модулях
    for (let i = moduleIndex - 1; i >= 0; i--) {
      const prevModule = courseData.modules[i].module || courseData.modules[i];
      const prevModuleTests = getAllTestsFromModule(prevModule);

      // Сначала проверяем тесты в предыдущем модуле
      if (prevModuleTests.length > 0) {
        const lastTestIndex = prevModuleTests.length - 1;
        const prevTestId = prevModuleTests[lastTestIndex]._id;
        navigate(
          `/courses/${courseData.slug}/modules/${prevModule.slug || prevModule._id}/tests/${prevTestId}/preview`
        );
        return;
      }

      // Если тестов нет, проверяем уроки
      if (prevModule?.lessons && prevModule.lessons.length > 0) {
        const lastLessonIndex = prevModule.lessons.length - 1;
        const prevLesson = prevModule.lessons[lastLessonIndex];
        const prevLessonSlug = prevLesson.slug || prevLesson._id;
        navigate(
          `/courses/${courseData.slug}/modules/${prevModule.slug || prevModule._id}/lessons/${prevLessonSlug}`
        );
        return;
      }
    }
  };

  // Проверка наличия следующего теста или урока
  const checkHasNextTest = useCallback(() => {
    if (
      !courseData ||
      !courseData.modules ||
      moduleIndex === null ||
      !currentTest
    )
      return false;

    const currentModule = courseData.modules[moduleIndex].module || courseData.modules[moduleIndex];
    const currentModuleTests = getAllTestsFromModule(currentModule);

    // Находим текущий тест в списке всех тестов модуля
    const currentTestIdxInModule = currentModuleTests.findIndex(t => t._id === currentTest._id);

    // Проверяем, есть ли следующий тест в текущем модуле
    if (currentTestIdxInModule < currentModuleTests.length - 1) {
      return true;
    }

    // Проверяем, есть ли тесты или уроки в следующих модулях
    for (let i = moduleIndex + 1; i < courseData.modules.length; i++) {
      const nextModule = courseData.modules[i].module || courseData.modules[i];
      const nextModuleTests = getAllTestsFromModule(nextModule);
      if (
        nextModuleTests.length > 0 ||
        (nextModule?.lessons && nextModule.lessons.length > 0)
      ) {
        return true;
      }
    }

    return false;
  }, [courseData, moduleIndex, currentTest]);

  // Определяем тип следующего элемента
  const getNextItemType = useCallback(() => {
    if (
      !courseData ||
      !courseData.modules ||
      moduleIndex === null ||
      !currentTest
    )
      return null;

    const currentModule = courseData.modules[moduleIndex].module || courseData.modules[moduleIndex];
    const currentModuleTests = getAllTestsFromModule(currentModule);

    // Находим текущий тест в списке всех тестов модуля
    const currentTestIdxInModule = currentModuleTests.findIndex(t => t._id === currentTest._id);

    // Если есть следующий тест в текущем модуле
    if (currentTestIdxInModule < currentModuleTests.length - 1) {
      return 'test';
    }

    // Ищем в следующих модулях
    for (let i = moduleIndex + 1; i < courseData.modules.length; i++) {
      const nextModule = courseData.modules[i].module || courseData.modules[i];
      const nextModuleTests = getAllTestsFromModule(nextModule);

      if (nextModule?.lessons && nextModule.lessons.length > 0) {
        return 'lesson';
      }
      if (nextModuleTests.length > 0) {
        return 'test';
      }
    }

    return null;
  }, [courseData, moduleIndex, currentTest]);

  // Определяем тип предыдущего элемента
  const getPrevItemType = useCallback(() => {
    if (
      !courseData ||
      !courseData.modules ||
      moduleIndex === null ||
      !currentTest
    )
      return null;

    const currentModule = courseData.modules[moduleIndex].module || courseData.modules[moduleIndex];
    const currentModuleTests = getAllTestsFromModule(currentModule);

    // Находим текущий тест в списке всех тестов модуля
    const currentTestIdxInModule = currentModuleTests.findIndex(t => t._id === currentTest._id);

    // Если есть предыдущий тест в текущем модуле
    if (currentTestIdxInModule > 0) {
      return 'test';
    }

    // Если это первый тест в модуле, проверяем уроки в текущем модуле
    if (currentModule?.lessons && currentModule.lessons.length > 0) {
      return 'lesson';
    }

    // Ищем в предыдущих модулях
    for (let i = moduleIndex - 1; i >= 0; i--) {
      const prevModule = courseData.modules[i].module || courseData.modules[i];
      const prevModuleTests = getAllTestsFromModule(prevModule);

      if (prevModuleTests.length > 0) {
        return 'test';
      }
      if (prevModule?.lessons && prevModule.lessons.length > 0) {
        return 'lesson';
      }
    }

    return null;
  }, [courseData, moduleIndex, currentTest]);

  // Проверка наличия предыдущего теста или урока
  const checkHasPrevTest = useCallback(() => {
    if (
      !courseData ||
      !courseData.modules ||
      moduleIndex === null ||
      !currentTest
    )
      return false;

    const currentModule = courseData.modules[moduleIndex].module || courseData.modules[moduleIndex];
    const currentModuleTests = getAllTestsFromModule(currentModule);

    // Находим текущий тест в списке всех тестов модуля
    const currentTestIdxInModule = currentModuleTests.findIndex(t => t._id === currentTest._id);

    // Проверяем, есть ли предыдущий тест в текущем модуле
    if (currentTestIdxInModule > 0) {
      return true;
    }

    // Если это первый тест в модуле, проверяем уроки в текущем модуле
    if (currentModule?.lessons && currentModule.lessons.length > 0) {
      return true;
    }

    // Проверяем, есть ли тесты или уроки в предыдущих модулях
    for (let i = moduleIndex - 1; i >= 0; i--) {
      const prevModule = courseData.modules[i].module || courseData.modules[i];
      const prevModuleTests = getAllTestsFromModule(prevModule);

      if (
        prevModuleTests.length > 0 ||
        (prevModule?.lessons && prevModule.lessons.length > 0)
      ) {
        return true;
      }
    }

    return false;
  }, [courseData, moduleIndex, currentTest]);

  // Вспомогательная функция для поиска и перехода к следующему модулю
  function proceedToNextModuleOrLesson(data) {
    // Ищем следующий доступный модуль с тестами или уроками
    for (let i = moduleIndex + 1; i < data.modules.length; i++) {
      const nextModule = data.modules[i].module || data.modules[i];

      // Сначала проверяем уроки в следующем модуле
      if (nextModule?.lessons && nextModule.lessons.length > 0) {
        const nextLesson = nextModule.lessons[0];
        const nextLessonSlug = nextLesson.slug || nextLesson._id;
        navigate(
          `/courses/${courseData.slug}/modules/${nextModule.slug || nextModule._id}/lessons/${nextLessonSlug}`
        );
        return true;
      }

      // Если уроков нет, проверяем тесты в уроках
      const nextModuleTests = getAllTestsFromModule(nextModule);
      if (nextModuleTests.length > 0) {
        const nextTestId = nextModuleTests[0]._id;
        navigate(
          `/courses/${courseData.slug}/modules/${nextModule.slug || nextModule._id}/tests/${nextTestId}/preview`
        );
        return true;
      }
    }

    // Если не нашли следующий модуль с тестами или уроками, показываем сообщение
    toast.info(t('tests:testDetail.testPreviewPage.messages.endOfCourse'));
    return false;
  }

  if (loading) {
    return (
      <Container sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button variant="text" onClick={handleBackToCourse}>
            {t('common:actions.back')}
          </Button>
        </Box>
      </Container>
    );
  }

  // Если нет доступа, показываем сообщение об ошибке
  if (!hasAccess) {
    return (
      <Container sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('common:errors.accessDenied')}
        </Alert>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button variant="text" onClick={() => navigate('/')}>
            {t('common:actions.backToHome')}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      {/* Убираем кнопки редактирования для админов при просмотре тестов */}

      <TestDetail
        ref={testDetailRef}
        courseId={courseData?._id}
        moduleId={moduleSlug}
        testId={testId}
        onBackClick={handleBackToCourse}
        onNextTest={handleNextTest}
        onPrevTest={handlePrevTest}
        hasNextTest={checkHasNextTest()}
        hasPrevTest={checkHasPrevTest()}
        nextItemType={getNextItemType()}
        prevItemType={getPrevItemType()}
        testData={
          currentTest
            ? {
                test: currentTest,
                moduleName: currentModule?.module?.title || currentModule?.title,
                courseName: courseData?.title,
                progress: courseData?.userProgress?.progress,
              }
            : null
        }
      />
    </Container>
  );
};

export default TestPreviewPage;
